import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft, Loader2, CheckCircle2, XCircle, Trophy, RotateCcw,
  ChevronRight, ChevronLeft, Target, Award, Sparkles
} from "lucide-react";
import { authGet, authPost, isAuthenticated } from "../utils/auth";
import { toast } from "sonner";

export default function TrainingQuiz() {
  const { moduleId } = useParams();
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [moduleTitle, setModuleTitle] = useState("");
  const [loading, setLoading] = useState(true);
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [showReview, setShowReview] = useState(false);

  useEffect(() => {
    if (!isAuthenticated()) { navigate("/advisor/login"); return; }
    fetchQuiz();
  }, [moduleId]);

  const fetchQuiz = async () => {
    try {
      const res = await authGet(`/api/training/quiz/${moduleId}`);
      if (res.ok) {
        setQuestions(res.data.questions || []);
        setModuleTitle(res.data.module_title || "");
        setAnswers(new Array(res.data.questions?.length || 0).fill(-1));
      } else {
        toast.error("Failed to load quiz");
      }
    } catch (e) { toast.error("Failed to load quiz"); }
    setLoading(false);
  };

  const selectAnswer = (qIdx, optIdx) => {
    if (submitted) return;
    const newAnswers = [...answers];
    newAnswers[qIdx] = optIdx;
    setAnswers(newAnswers);
  };

  const handleSubmit = async () => {
    const unanswered = answers.filter(a => a === -1).length;
    if (unanswered > 0) {
      toast.error(`Please answer all questions (${unanswered} remaining)`);
      return;
    }
    setSubmitting(true);
    try {
      const res = await authPost(`/api/training/quiz/${moduleId}/submit`, { answers });
      if (res.ok) {
        setResult(res.data);
        setSubmitted(true);
      }
    } catch (e) { toast.error("Submit failed"); }
    setSubmitting(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#FFFFFF] to-[#EDF1F8] flex flex-col items-center justify-center gap-3">
        <Loader2 className="h-8 w-8 text-[#A87608] animate-spin" />
        <p className="text-[#3A4557] text-sm">Generating quiz questions...</p>
      </div>
    );
  }

  if (submitted && result && !showReview) {
    return <QuizResult result={result} moduleTitle={moduleTitle} moduleId={moduleId}
      navigate={navigate} onReview={() => setShowReview(true)} />;
  }

  const q = questions[currentQ];
  const answeredCount = answers.filter(a => a !== -1).length;

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FFFFFF] to-[#EDF1F8]">
      <div className="max-w-3xl mx-auto px-4 py-6 pb-32">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => navigate(`/advisor/training/${moduleId}`)}
            className="text-[#3A4557] hover:text-[#1B2A4A] transition-colors" data-testid="back-from-quiz">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <p className="text-xs text-[#3A4557]">{moduleTitle}</p>
            <h1 className="text-lg font-bold text-[#1B2A4A]">Module Quiz</h1>
          </div>
        </div>

        {/* Progress Dots */}
        <div className="flex gap-1.5 mb-6" data-testid="quiz-progress-dots">
          {questions.map((_, i) => (
            <button key={i} onClick={() => setCurrentQ(i)}
              className={`h-2 flex-1 rounded-full transition-all ${
                i === currentQ ? "bg-[#D4A017]" : answers[i] !== -1 ? "bg-emerald-500" : "bg-[#E2E9F4]"
              } ${submitted && result?.results?.[i] ? (result.results[i].is_correct ? "bg-emerald-500" : "bg-red-500") : ""}`}
            />
          ))}
        </div>

        {/* Question Card */}
        <AnimatePresence mode="wait">
          {q && (
            <motion.div key={currentQ}
              initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }}
              transition={{ duration: 0.2 }}
              className="bg-[#EDF1F8]/40 border border-[#C9D4E6]/50 rounded-2xl p-6 mb-6"
              data-testid="quiz-question-card"
            >
              <div className="flex items-center gap-2 mb-4">
                <span className="text-xs bg-[#D4A017]/15 text-[#A87608] px-2 py-0.5 rounded-full font-medium">
                  Q{currentQ + 1}/{questions.length}
                </span>
                {submitted && result?.results?.[currentQ] && (
                  result.results[currentQ].is_correct
                    ? <span className="text-xs bg-emerald-500/20 text-emerald-600 px-2 py-0.5 rounded-full">Correct</span>
                    : <span className="text-xs bg-red-500/20 text-red-600 px-2 py-0.5 rounded-full">Incorrect</span>
                )}
              </div>
              <p className="text-[#1B2A4A] font-medium text-base mb-5">{q.question}</p>

              <div className="space-y-3">
                {q.options.map((opt, optIdx) => {
                  const isSelected = answers[currentQ] === optIdx;
                  const isCorrect = submitted && result?.results?.[currentQ]?.correct_answer === optIdx;
                  const isWrong = submitted && isSelected && !result?.results?.[currentQ]?.is_correct;
                  
                  return (
                    <button key={optIdx} onClick={() => selectAnswer(currentQ, optIdx)}
                      disabled={submitted}
                      className={`w-full text-left p-4 rounded-xl border transition-all ${
                        isCorrect
                          ? "bg-emerald-500/10 border-emerald-500/40 text-emerald-700"
                          : isWrong
                            ? "bg-red-500/10 border-red-500/40 text-red-700"
                            : isSelected
                              ? "bg-[#D4A017]/10 border-[#D4A017]/40 text-[#A87608]"
                              : "bg-white/50 border-[#C9D4E6]/50 text-[#2E3B57] hover:border-[#C9D4E6]"
                      }`}
                      data-testid={`quiz-option-${optIdx}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center shrink-0 ${
                          isCorrect ? "border-emerald-400 bg-emerald-500/20" :
                          isWrong ? "border-red-400 bg-red-500/20" :
                          isSelected ? "border-[#D4A017] bg-[#D4A017]/15" : "border-[#C9D4E6]"
                        }`}>
                          {isCorrect && <CheckCircle2 className="h-4 w-4 text-emerald-600" />}
                          {isWrong && <XCircle className="h-4 w-4 text-red-600" />}
                          {isSelected && !submitted && <div className="w-2.5 h-2.5 rounded-full bg-[#D4A017]" />}
                        </div>
                        <span className="text-sm">{opt}</span>
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Explanation after submit */}
              {submitted && result?.results?.[currentQ]?.explanation && (
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                  className="mt-4 p-3 bg-white/50 rounded-lg border border-[#C9D4E6]/50">
                  <p className="text-xs text-[#3A4557] uppercase tracking-wider mb-1">Explanation</p>
                  <p className="text-sm text-[#2E3B57]">{result.results[currentQ].explanation}</p>
                </motion.div>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Navigation */}
        <div className="flex gap-3">
          <button onClick={() => setCurrentQ(Math.max(0, currentQ - 1))} disabled={currentQ === 0}
            className="flex-1 py-3 bg-[#EDF1F8]/50 border border-[#C9D4E6]/50 rounded-xl text-[#3A4557] hover:text-[#1B2A4A] disabled:opacity-30 text-sm font-medium transition-colors flex items-center justify-center gap-1"
            data-testid="quiz-prev-btn">
            <ChevronLeft className="h-4 w-4" /> Previous
          </button>
          {currentQ < questions.length - 1 ? (
            <button onClick={() => setCurrentQ(currentQ + 1)}
              className="flex-1 py-3 bg-[#D4A017]/10 border border-[#D4A017]/30 rounded-xl text-[#A87608] hover:bg-[#D4A017]/15 text-sm font-medium transition-colors flex items-center justify-center gap-1"
              data-testid="quiz-next-btn">
              Next <ChevronRight className="h-4 w-4" />
            </button>
          ) : !submitted ? (
            <button onClick={handleSubmit} disabled={submitting}
              className="flex-1 py-3 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded-xl text-sm font-bold transition-colors flex items-center justify-center gap-1"
              data-testid="quiz-submit-btn">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
              {submitting ? "Submitting..." : `Submit (${answeredCount}/${questions.length})`}
            </button>
          ) : (
            <button onClick={() => navigate(`/advisor/training/${moduleId}`)}
              className="flex-1 py-3 bg-[#D4A017]/10 border border-[#D4A017]/30 rounded-xl text-[#A87608] text-sm font-medium transition-colors"
              data-testid="quiz-done-btn">
              Done
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function QuizResult({ result, moduleTitle, moduleId, navigate, onReview }) {
  const isPassed = result.passed;
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FFFFFF] to-[#EDF1F8] flex items-center justify-center px-4">
      <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
        transition={{ type: "spring", duration: 0.5 }}
        className="w-full max-w-md"
      >
        <div className={`rounded-2xl border p-8 text-center ${
          isPassed
            ? "bg-gradient-to-b from-emerald-500/10 to-[#EDF1F8] border-emerald-500/30"
            : "bg-gradient-to-b from-red-500/10 to-[#EDF1F8] border-red-500/30"
        }`} data-testid="quiz-result-card">
          {/* Icon */}
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.2, type: "spring" }}
            className={`w-20 h-20 rounded-full mx-auto flex items-center justify-center mb-4 ${
              isPassed ? "bg-emerald-500/20" : "bg-red-500/20"
            }`}>
            {isPassed ? <Trophy className="h-10 w-10 text-emerald-600" /> : <Target className="h-10 w-10 text-red-600" />}
          </motion.div>

          <h2 className={`text-2xl font-bold mb-1 ${isPassed ? "text-emerald-600" : "text-red-600"}`}>
            {isPassed ? "Congratulations!" : "Keep Learning!"}
          </h2>
          <p className="text-[#3A4557] text-sm mb-6">{moduleTitle}</p>

          {/* Score */}
          <div className="bg-[#EDF1F8]/50 rounded-xl p-4 mb-6">
            <p className="text-4xl font-bold text-[#1B2A4A] mb-1">{result.score}%</p>
            <p className="text-sm text-[#3A4557]">
              {result.correct_count} out of {result.total_questions} correct
            </p>
            <p className={`text-xs mt-2 font-medium ${isPassed ? "text-emerald-600" : "text-[#A87608]"}`}>
              {isPassed ? "You passed! Module completed." : "You need 70% to pass. Review and try again!"}
            </p>
          </div>

          {/* Actions */}
          <div className="space-y-3">
            <button onClick={onReview}
              className="w-full py-3 bg-[#EDF1F8]/50 border border-[#C9D4E6]/50 rounded-xl text-[#1B2A4A] font-medium text-sm hover:bg-[#EDF1F8]/80 transition-colors"
              data-testid="review-answers-btn">
              Review Answers
            </button>
            {!isPassed && (
              <button onClick={() => { window.location.reload(); }}
                className="w-full py-3 bg-[#D4A017]/10 border border-[#D4A017]/30 rounded-xl text-[#A87608] font-medium text-sm hover:bg-[#D4A017]/15 transition-colors flex items-center justify-center gap-2"
                data-testid="retake-quiz-btn">
                <RotateCcw className="h-4 w-4" /> Retake Quiz
              </button>
            )}
            <button onClick={() => navigate("/advisor/training")}
              className="w-full py-3 text-[#3A4557] text-sm hover:text-[#1B2A4A] transition-colors"
              data-testid="back-to-academy-btn">
              Back to Academy
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
